import {
    CalculationOptions,
    CalculationTrace,
    CalculationWarning,
    NormalizedInput,
    SajuCalculationResult,
    SajuInput,
    SajuValidationPayload
} from './types';
import { SajuDerivedCalculator } from './derived-calculations';
import { generateChartSignature } from './chart-signature';
import { generateInterpretationSignals } from './interpretation-signals';

export const SAJU_ENGINE_VERSION = 'saju-calculator-0.1.0';
export const SAJU_RULESET_VERSION = 'baseline-ruleset-2026-05-24';

export class SajuCalculator {
    private derivedCalculator: SajuDerivedCalculator = new SajuDerivedCalculator(this);

    public calculate(input: SajuInput): SajuCalculationResult {
        let options = this.calculationOptions(input);
        let date = input.birthDate instanceof Date
            ? new Date(input.birthDate.getFullYear(), input.birthDate.getMonth(), input.birthDate.getDate())
            : this.parseBirthDate(String(input.birthDate || ''), options.calendarType);
        let warnings: CalculationWarning[] = [];

        if (!date) {
            warnings.push({
                code: 'invalid_birth_date',
                message: '생년월일을 해석할 수 없어 오늘 날짜로 계산했습니다.',
                severity: 'error'
            });
            date = new Date();
        }

        let chart = this.buildChart(date, options.birthTime, options);
        let pillars = chart.pillars;
        let rawElements = this.elementCounts(pillars);
        let elements = this.weightedElementDistribution(pillars);
        let tenGods = this.tenGodDistribution(pillars);
        let elementRelations = this.elementRelations(pillars[2]?.stem?.element);
        let luckInfo = this.majorLuckInfo(chart, options.gender);
        let derived = this.derivedCalculator.buildDerivedData(pillars, luckInfo, options.targetYear);
        let normalizedInput = this.normalizedInput(input, options, chart, date);

        if (options.timeUnknown) {
            warnings.push({
                code: 'time_unknown',
                message: '출생 시간이 없어 시주는 계산하지 않고 정오 기준으로 절기와 날짜만 보정했습니다.',
                severity: 'warning'
            });
        }
        if (options.calendarType === 'lunar' || options.calendarType === 'lunarLeap') {
            warnings.push({
                code: 'lunar_conversion_baseline',
                message: '음력 변환은 현재 Intl 달력 기반 baseline 로직입니다. 별도 gold sample 검증이 필요합니다.',
                severity: 'info'
            });
        }

        let trace = this.trace(chart, pillars, options);
        let confidence = this.confidence(warnings);
        let baseValidation = this.validationPayload(this.rawInput(input), normalizedInput, options, warnings, confidence, trace);
        let validation = this.derivedCalculator.enrichValidationPayload(baseValidation, pillars, elements, tenGods, luckInfo, derived);

        let result: SajuCalculationResult = {
            input: input,
            calculationOptions: options,
            normalizedInput: normalizedInput,
            chart: chart,
            pillars: pillars,
            pillarDetails: derived.pillarDetails,
            rawElements: rawElements,
            elements: elements,
            elementRelations: elementRelations,
            tenGods: tenGods,
            luckInfo: luckInfo,
            luckFlow: derived.luckFlow,
            annualLuck: derived.annualLuck,
            monthlyLuck: derived.monthlyLuck,
            interactions: derived.interactions,
            nobleStars: derived.nobleStars,
            specialStars: derived.specialStars,
            emptyBranches: derived.emptyBranches,
            warnings: warnings,
            confidence: confidence,
            trace: trace,
            validation: validation,
            interpretationSignals: []
        };
        result.interpretationSignals = this.generateInterpretationSignals(result);
        result.chartSignature = generateChartSignature(result, result.interpretationSignals);
        result.validation = {
            ...result.validation,
            interpretationSignals: result.interpretationSignals,
            chartSignature: result.chartSignature
        };
        return result;
    }

    public generateInterpretationSignals(result: SajuCalculationResult) {
        return generateInterpretationSignals(result);
    }

    public elementRelations(dayElement: string) {
        return {
            dayElement: dayElement,
            outputElement: this.producesElement(dayElement),
            wealthElement: this.controlsElement(dayElement),
            resourceElement: this.producerOf(dayElement),
            officerElement: this.controlledByElement(dayElement)
        };
    }

    public calculationOptions(input: Partial<SajuInput> = {}): CalculationOptions {
        return {
            calendarType: input.calendarType || 'solar',
            birthTime: input.birthTime || '12:00',
            timeUnknown: !!input.timeUnknown,
            birthPlace: input.birthPlace || 'none',
            gender: input.gender || 'none',
            targetYear: input.targetYear
        };
    }

    public normalizedInput(input: SajuInput, options: CalculationOptions, chart: any, solarDate: Date): NormalizedInput {
        return {
            rawBirthDate: input.birthDate instanceof Date ? this.formatInputDate(input.birthDate) : String(input.birthDate || ''),
            calendarType: options.calendarType,
            solarDate: this.formatInputDate(solarDate),
            birthTime: options.birthTime,
            timeUnknown: options.timeUnknown,
            originalDateTime: chart.meta.originalDateTime,
            adjustedDateTime: chart.meta.adjustedDateTime,
            timeCorrectionMinutes: chart.meta.timeCorrectionMinutes,
            birthPlace: options.birthPlace,
            birthPlaceLabel: this.birthPlaceLabel(options.birthPlace),
            timezone: 'Asia/Seoul'
        };
    }

    public rawInput(input: SajuInput): Record<string, unknown> {
        return {
            name: input.name || '',
            birthDate: input.birthDate instanceof Date ? this.formatInputDate(input.birthDate) : String(input.birthDate || ''),
            birthTime: input.birthTime || '12:00',
            timeUnknown: !!input.timeUnknown,
            calendarType: input.calendarType || 'solar',
            birthPlace: input.birthPlace || 'none',
            gender: input.gender || 'none',
            targetYear: input.targetYear
        };
    }

    public validationPayload(
        rawInput: Record<string, unknown>,
        normalizedInput: NormalizedInput,
        calculationOptions: CalculationOptions,
        warnings: CalculationWarning[],
        confidence: number,
        trace: CalculationTrace
    ): SajuValidationPayload {
        return {
            engineVersion: SAJU_ENGINE_VERSION,
            ruleSetVersion: SAJU_RULESET_VERSION,
            rawInput: rawInput,
            normalizedInput: normalizedInput,
            calculationOptions: calculationOptions,
            warnings: warnings,
            confidence: confidence,
            trace: trace
        };
    }

    public trace(chart: any, pillars: any[], options: CalculationOptions): CalculationTrace {
        return {
            steps: [
                { id: 'calendar', label: '달력 입력', value: this.calendarTypeLabel(options.calendarType) },
                { id: 'original_datetime', label: '원본 일시', value: chart.meta.originalDateTime },
                { id: 'adjusted_datetime', label: '보정 일시', value: chart.meta.adjustedDateTime, note: `${chart.meta.timeCorrectionMinutes}분 보정` },
                { id: 'year_basis', label: '연주 기준', value: chart.meta.yearBasis },
                { id: 'month_term', label: '월주 절기', value: chart.meta.monthTerm, note: chart.meta.monthTermDateTime },
                { id: 'pillars', label: '사주팔자', value: pillars.map((pillar: any) => pillar.label).join(' · ') },
                { id: 'time_unknown', label: '생시 미정', value: options.timeUnknown }
            ]
        };
    }

    public confidence(warnings: CalculationWarning[]) {
        let score = 1;
        for (let warning of warnings) {
            if (warning.severity === 'error') score -= 0.4;
            if (warning.severity === 'warning') score -= 0.15;
            if (warning.severity === 'info') score -= 0.05;
        }
        return Math.max(0, Math.round(score * 100) / 100);
    }

    public buildPillars(date: Date, time: string = '12:00') {
        return this.buildChart(date, time).pillars;
    }

    public buildChart(date: Date, time: string = '12:00', options: any = {}) {
        let timeUnknown = !!options.timeUnknown;
        let clock = this.adjustBirthDateTime(date, timeUnknown ? '12:00' : time, options);
        let adjusted = clock.adjusted;
        let yearIndex = this.yearGanjiIndex(adjusted);
        let yearPillar = this.ganjiFromIndex(yearIndex);
        let monthTerm = this.findSolarMonthTerm(adjusted);
        let monthStemIndex = this.monthStemIndex(yearPillar.stem.index, monthTerm.branchIndex);
        let dayPillar = this.ganjiFromIndex(this.dayGanjiIndex(adjusted));
        let pillars = [
            this.pillar('년주', yearPillar.stem.index, yearPillar.branch.index),
            this.pillar('월주', monthStemIndex, monthTerm.branchIndex),
            this.pillar('일주', dayPillar.stem.index, dayPillar.branch.index)
        ];
        if (timeUnknown) {
            pillars.push(this.unknownHourPillar());
        } else {
            let hourBranchIndex = this.hourBranchIndex(adjusted.getHours(), adjusted.getMinutes());
            let hourStemIndex = this.hourStemIndex(dayPillar.stem.index, hourBranchIndex);
            pillars.push(this.pillar('시주', hourStemIndex, hourBranchIndex));
        }
        let timeSummary = timeUnknown
            ? `${this.formatInputDate(date)} 생시 미정`
            : `출생시 ${this.formatDateTime(clock.original)} → 사주시 ${this.formatDateTime(adjusted)} · 보정 ${clock.correctionMinutes}분`;

        return {
            pillars: pillars,
            original: clock.original,
            adjusted: adjusted,
            monthTerm: monthTerm,
            meta: {
                originalDateTime: this.formatDateTime(clock.original),
                adjustedDateTime: this.formatDateTime(adjusted),
                timeCorrectionMinutes: clock.correctionMinutes,
                timeUnknown: timeUnknown,
                timeSummary: timeSummary,
                monthTerm: monthTerm.name,
                monthTermDateTime: this.formatDateTime(monthTerm.date),
                yearBasis: `${this.solarTermDate(adjusted.getFullYear(), 2).getMonth() + 1}월 ${this.solarTermDate(adjusted.getFullYear(), 2).getDate()}일 입춘 기준`,
                birthPlace: this.birthPlaceLabel(options.birthPlace || 'none')
            }
        };
    }

    public pillar(part: string, stemIndex: number, branchIndex: number) {
        let stems = this.stemData();
        let branches = this.branchData();
        let stem = stems[this.mod(stemIndex, stems.length)];
        let branch = branches[this.mod(branchIndex, branches.length)];
        let cycleIndex = this.cycleIndex(stem.index, branch.index);
        return {
            part: part,
            label: `${stem.name}${branch.name}`,
            stem: stem,
            branch: branch,
            index: this.mod(stem.index, 10) + this.mod(branch.index, 12),
            cycleIndex: cycleIndex
        };
    }

    public ganjiFromIndex(index: number) {
        return this.pillar('', index, index);
    }

    public unknownHourPillar() {
        return {
            part: '시주',
            label: '미정',
            unknown: true,
            stem: { index: -1, name: '', hanja: '', element: '', yinYang: '' },
            branch: { index: -1, name: '', hanja: '', animal: '', element: '' },
            index: -1,
            cycleIndex: -1
        };
    }

    public adjustBirthDateTime(date: Date, time: string = '12:00', options: any = {}) {
        let parts = this.timeParts(time);
        let original = new Date(date.getFullYear(), date.getMonth(), date.getDate(), parts.hour, parts.minute);
        let correctionMinutes = 0;
        let birthPlace = options.birthPlace || 'none';

        if (birthPlace !== 'none' && !this.isKoreanLocalStandardPeriod(original)) {
            correctionMinutes += this.birthPlaceCorrectionMinutes(birthPlace);
        }

        if (this.isKoreanDaylightSavingPeriod(original)) {
            correctionMinutes -= 60;
        }

        return {
            original: original,
            adjusted: new Date(original.getTime() + correctionMinutes * 60000),
            correctionMinutes: correctionMinutes
        };
    }

    public birthPlaceCorrectionMinutes(place: string = 'none') {
        let places: any = {
            none: 0,
            seoul: -32,
            busan: -24,
            daegu: -26,
            gwangju: -33,
            daejeon: -30,
            jeju: -34
        };
        return places[place] ?? places.none;
    }

    public birthPlaceLabel(place: string = 'none') {
        let labels: any = {
            none: '표준시 기준',
            seoul: '서울/수도권 경도 보정',
            busan: '부산/경남 경도 보정',
            daegu: '대구/경북 경도 보정',
            gwangju: '광주/전라 경도 보정',
            daejeon: '대전/충청 경도 보정',
            jeju: '제주 경도 보정'
        };
        return labels[place] || labels.none;
    }

    public timeParts(time: string) {
        let parts = String(time || '12:00').split(':');
        let hour = parseInt(parts[0], 10);
        let minute = parseInt(parts[1], 10);
        return {
            hour: Number.isNaN(hour) ? 12 : this.clamp(hour, 0, 23),
            minute: Number.isNaN(minute) ? 0 : this.clamp(minute, 0, 59)
        };
    }

    public isKoreanLocalStandardPeriod(date: Date) {
        let value = this.dateNumber(date);
        return this.isDateNumberBetween(value, 19080401, 19111231) || this.isDateNumberBetween(value, 19540321, 19610809);
    }

    public isKoreanDaylightSavingPeriod(date: Date) {
        let ranges = [
            [19480601, 19480913],
            [19490403, 19490911],
            [19500401, 19500910],
            [19510506, 19510909],
            [19550505, 19550909],
            [19560520, 19560930],
            [19570505, 19570922],
            [19580504, 19580921],
            [19590503, 19590920],
            [19600501, 19600918],
            [19870510, 19871011],
            [19880508, 19881009]
        ];
        let value = this.dateNumber(date);
        return ranges.some((range: number[]) => this.isDateNumberBetween(value, range[0], range[1]));
    }

    public dateNumber(date: Date) {
        let month = date.getMonth() + 1;
        let day = date.getDate();
        return date.getFullYear() * 10000 + month * 100 + day;
    }

    public isDateNumberBetween(value: number, start: number, end: number) {
        return value >= start && value <= end;
    }

    public yearGanjiIndex(date: Date) {
        let year = date.getFullYear();
        let ipchun = this.solarTermDate(year, 2);
        if (date.getTime() < ipchun.getTime()) {
            year -= 1;
        }
        return year - 4;
    }

    public solarTermInfo() {
        return [
            { index: 0, name: '소한', minutes: 0, branchIndex: 1 },
            { index: 1, name: '대한', minutes: 21208, branchIndex: 1 },
            { index: 2, name: '입춘', minutes: 42467, branchIndex: 2 },
            { index: 3, name: '우수', minutes: 63836, branchIndex: 2 },
            { index: 4, name: '경칩', minutes: 85337, branchIndex: 3 },
            { index: 5, name: '춘분', minutes: 107014, branchIndex: 3 },
            { index: 6, name: '청명', minutes: 128867, branchIndex: 4 },
            { index: 7, name: '곡우', minutes: 150921, branchIndex: 4 },
            { index: 8, name: '입하', minutes: 173149, branchIndex: 5 },
            { index: 9, name: '소만', minutes: 195551, branchIndex: 5 },
            { index: 10, name: '망종', minutes: 218072, branchIndex: 6 },
            { index: 11, name: '하지', minutes: 240693, branchIndex: 6 },
            { index: 12, name: '소서', minutes: 263343, branchIndex: 7 },
            { index: 13, name: '대서', minutes: 285989, branchIndex: 7 },
            { index: 14, name: '입추', minutes: 308563, branchIndex: 8 },
            { index: 15, name: '처서', minutes: 331033, branchIndex: 8 },
            { index: 16, name: '백로', minutes: 353350, branchIndex: 9 },
            { index: 17, name: '추분', minutes: 375494, branchIndex: 9 },
            { index: 18, name: '한로', minutes: 397447, branchIndex: 10 },
            { index: 19, name: '상강', minutes: 419210, branchIndex: 10 },
            { index: 20, name: '입동', minutes: 440795, branchIndex: 11 },
            { index: 21, name: '소설', minutes: 462224, branchIndex: 11 },
            { index: 22, name: '대설', minutes: 483532, branchIndex: 0 },
            { index: 23, name: '동지', minutes: 504758, branchIndex: 0 }
        ];
    }

    public solarTermDate(year: number, termIndex: number) {
        let term = this.solarTermInfo()[this.mod(termIndex, 24)];
        let utcTime = Date.UTC(1900, 0, 6, 2, 5) + 31556925974.7 * (year - 1900) + term.minutes * 60000;
        let kst = new Date(utcTime + 9 * 60 * 60000);
        return new Date(kst.getUTCFullYear(), kst.getUTCMonth(), kst.getUTCDate(), kst.getUTCHours(), kst.getUTCMinutes());
    }

    public solarMonthTerms(year: number) {
        let changeTerms = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22];
        return changeTerms.map((termIndex: number) => {
            let info = this.solarTermInfo()[termIndex];
            return {
                index: info.index,
                name: info.name,
                branchIndex: info.branchIndex,
                date: this.solarTermDate(year, termIndex)
            };
        });
    }

    public solarTermTimeline(year: number) {
        return this.solarTermInfo().map((info: any) => {
            let date = this.solarTermDate(year, info.index);
            return {
                index: info.index,
                name: info.name,
                type: info.index % 2 === 0 ? '절입' : '중기',
                branch: this.solarTermBranchLabel(info),
                date: date,
                dateText: this.formatDateTime(date)
            };
        });
    }

    public findSolarMonthTerm(date: Date) {
        let year = date.getFullYear();
        let terms = this.solarMonthTerms(year - 1).concat(this.solarMonthTerms(year), this.solarMonthTerms(year + 1));
        let passed = terms
            .filter((term: any) => term.date.getTime() <= date.getTime())
            .sort((a: any, b: any) => b.date.getTime() - a.date.getTime());
        return passed[0] || terms[0];
    }

    public nextSolarMonthTerm(date: Date) {
        let year = date.getFullYear();
        let terms = this.solarMonthTerms(year).concat(this.solarMonthTerms(year + 1));
        return terms
            .filter((term: any) => term.date.getTime() > date.getTime())
            .sort((a: any, b: any) => a.date.getTime() - b.date.getTime())[0];
    }

    public previousSolarMonthTerm(date: Date) {
        let year = date.getFullYear();
        let terms = this.solarMonthTerms(year - 1).concat(this.solarMonthTerms(year));
        return terms
            .filter((term: any) => term.date.getTime() < date.getTime())
            .sort((a: any, b: any) => b.date.getTime() - a.date.getTime())[0];
    }

    public nextSolarTerm(date: Date) {
        let year = date.getFullYear();
        let terms = this.solarTermTimeline(year).concat(this.solarTermTimeline(year + 1));
        return terms
            .filter((term: any) => term.date.getTime() > date.getTime())
            .sort((a: any, b: any) => a.date.getTime() - b.date.getTime())[0];
    }

    public previousSolarTerm(date: Date) {
        let year = date.getFullYear();
        let terms = this.solarTermTimeline(year - 1).concat(this.solarTermTimeline(year));
        return terms
            .filter((term: any) => term.date.getTime() < date.getTime())
            .sort((a: any, b: any) => b.date.getTime() - a.date.getTime())[0];
    }

    public solarTermBranchLabel(term: any) {
        let branch = this.branchData()[this.mod(term?.branchIndex ?? 0, 12)];
        return `${branch.name}${branch.hanja}`;
    }

    public dateTimeGapLabel(start: Date, end: Date) {
        let totalHours = Math.max(0, Math.round(Math.abs(end.getTime() - start.getTime()) / 3600000));
        return `${Math.floor(totalHours / 24)}일 ${totalHours % 24}시간`;
    }

    public monthStemIndex(yearStemIndex: number, monthBranchIndex: number) {
        let firstMonthStem = this.mod(this.mod(yearStemIndex, 5) * 2 + 2, 10);
        let monthOffset = this.mod(monthBranchIndex - 2, 12);
        return this.mod(firstMonthStem + monthOffset, 10);
    }

    public dayGanjiIndex(date: Date) {
        return this.daysSinceBase(date) + 2;
    }

    public hourBranchIndex(hour: number, minute: number = 0) {
        let normalizedHour = hour + minute / 60;
        return this.mod(Math.floor((normalizedHour + 1) / 2), 12);
    }

    public hourStemIndex(dayStemIndex: number, hourBranchIndex: number) {
        return this.mod(this.mod(dayStemIndex, 5) * 2 + hourBranchIndex, 10);
    }

    public cycleIndex(stemIndex: number, branchIndex: number) {
        for (let index = 0; index < 60; index++) {
            if (this.mod(index, 10) === this.mod(stemIndex, 10) && this.mod(index, 12) === this.mod(branchIndex, 12)) {
                return index;
            }
        }
        return this.mod(stemIndex, 60);
    }

    public majorLuckInfo(chart: any, gender: string = 'none') {
        let yearStem = chart.pillars[0].stem;
        let isYangYear = yearStem.yinYang === '양';
        let hasGender = gender === 'male' || gender === 'female';
        let forward = hasGender ? ((gender === 'male' && isYangYear) || (gender === 'female' && !isYangYear)) : true;
        let targetTerm = forward ? this.nextSolarMonthTerm(chart.adjusted) : this.previousSolarMonthTerm(chart.adjusted);
        let totalHours = targetTerm ? Math.round(Math.abs(targetTerm.date.getTime() - chart.adjusted.getTime()) / 3600000) : 0;
        let wholeDays = Math.floor(totalHours / 24);
        let remainHours = totalHours % 24;
        let startMonths = Math.max(1, Math.round((totalHours / 24) * 4));
        let startYears = Math.floor(startMonths / 12);
        let startRemainderMonths = startMonths % 12;
        let startAge = Math.max(1, startYears || 1);
        let preciseStartLabel = `${startYears}년 ${startRemainderMonths}개월 전후`;

        return {
            direction: forward ? '순행' : '역행',
            startAge: startAge,
            startMonths: startMonths,
            startAgeLabel: chart.meta?.timeUnknown ? `약 ${preciseStartLabel} 시작` : `${preciseStartLabel} 시작`,
            preciseStartLabel: preciseStartLabel,
            basisTerm: targetTerm?.name || '',
            basisDateTime: targetTerm ? this.formatDateTime(targetTerm.date) : '',
            daysToTerm: wholeDays,
            hoursToTerm: remainHours,
            gapLabel: `${wholeDays}일 ${remainHours}시간`,
            timeUnknown: !!chart.meta?.timeUnknown
        };
    }

    public pillarDetails(pillars: any[]) {
        return this.derivedCalculator.pillarDetails(pillars);
    }

    public buildInteractionSections(pillars: any[]) {
        return this.derivedCalculator.buildInteractionSections(pillars);
    }

    public findNobleStars(pillars: any[]) {
        return this.derivedCalculator.findNobleStars(pillars);
    }

    public findSpecialStars(pillars: any[]) {
        return this.derivedCalculator.findSpecialStars(pillars);
    }

    public majorLuckFlow(pillars: any[], luckInfo: any, length: number = 8) {
        return this.derivedCalculator.majorLuckFlow(pillars, luckInfo, length);
    }

    public annualLuckFlow(startYear: number, pillars: any[], length: number = 8) {
        return this.derivedCalculator.annualLuckFlow(startYear, pillars, length);
    }

    public monthlyLuckFlow(targetYear: number, pillars: any[]) {
        return this.derivedCalculator.monthlyLuckFlow(targetYear, pillars);
    }

    public shinsalName(baseBranch: string, targetBranch: string) {
        return this.derivedCalculator.shinsalName(baseBranch, targetBranch);
    }

    public twelveShinsalTable(baseBranch: string) {
        return this.derivedCalculator.twelveShinsalTable(baseBranch);
    }

    public specialStarTone(name: string) {
        return this.derivedCalculator.specialStarTone(name);
    }

    public nayin(cycleIndex: number) {
        return this.derivedCalculator.nayin(cycleIndex);
    }

    public weightedElementDistribution(pillars: any[]) {
        let counts: any = { 목: 0, 화: 0, 토: 0, 금: 0, 수: 0 };
        for (let pillar of pillars) {
            if (pillar?.unknown) continue;
            counts[pillar.stem.element] += 1;
            for (let hidden of this.hiddenStems(pillar.branch.name)) {
                counts[hidden.stem.element] += hidden.weight;
            }
        }
        let total = Object.keys(counts).reduce((sum: number, key: string) => sum + counts[key], 0);
        return Object.keys(counts).map((label: string) => ({
            label: label,
            value: Math.round(counts[label] * 10) / 10,
            percent: total ? Math.round((counts[label] / total) * 100) : 0,
            tone: this.elementTone(label).core
        })).sort((a: any, b: any) => b.value - a.value);
    }

    public tenGodDistribution(pillars: any[]) {
        let dayStem = pillars[2].stem;
        let labels = ['비견', '겁재', '식신', '상관', '편재', '정재', '편관', '정관', '편인', '정인', '일간'];
        let counts: any = {};
        labels.forEach((label: string) => counts[label] = 0);

        for (let pillar of pillars) {
            if (pillar?.unknown) continue;
            let stemGod = pillar.part === '일주' ? '일간' : this.tenGod(dayStem, pillar.stem);
            counts[stemGod] = (counts[stemGod] || 0) + 1;
            for (let hidden of this.hiddenStems(pillar.branch.name)) {
                let hiddenGod = this.tenGod(dayStem, hidden.stem);
                counts[hiddenGod] = (counts[hiddenGod] || 0) + hidden.weight;
            }
        }

        let total = Object.keys(counts).reduce((sum: number, key: string) => sum + counts[key], 0);
        return labels.map((label: string) => ({
            label: label,
            value: Math.round((counts[label] || 0) * 10) / 10,
            percent: total ? Math.round(((counts[label] || 0) / total) * 100) : 0,
            tone: this.tenGodTone(label)
        })).filter((item: any) => item.value > 0).sort((a: any, b: any) => b.value - a.value);
    }

    public hiddenStems(branchName: string) {
        let data: any = {
            자: [{ stem: '계', weight: 1, role: '정기' }],
            축: [{ stem: '기', weight: 0.6, role: '정기' }, { stem: '계', weight: 0.25, role: '중기' }, { stem: '신', weight: 0.15, role: '여기' }],
            인: [{ stem: '갑', weight: 0.6, role: '정기' }, { stem: '병', weight: 0.25, role: '중기' }, { stem: '무', weight: 0.15, role: '여기' }],
            묘: [{ stem: '을', weight: 1, role: '정기' }],
            진: [{ stem: '무', weight: 0.6, role: '정기' }, { stem: '을', weight: 0.25, role: '중기' }, { stem: '계', weight: 0.15, role: '여기' }],
            사: [{ stem: '병', weight: 0.6, role: '정기' }, { stem: '무', weight: 0.25, role: '중기' }, { stem: '경', weight: 0.15, role: '여기' }],
            오: [{ stem: '정', weight: 0.7, role: '정기' }, { stem: '기', weight: 0.3, role: '중기' }],
            미: [{ stem: '기', weight: 0.6, role: '정기' }, { stem: '정', weight: 0.25, role: '중기' }, { stem: '을', weight: 0.15, role: '여기' }],
            신: [{ stem: '경', weight: 0.6, role: '정기' }, { stem: '임', weight: 0.25, role: '중기' }, { stem: '무', weight: 0.15, role: '여기' }],
            유: [{ stem: '신', weight: 1, role: '정기' }],
            술: [{ stem: '무', weight: 0.6, role: '정기' }, { stem: '신', weight: 0.25, role: '중기' }, { stem: '정', weight: 0.15, role: '여기' }],
            해: [{ stem: '임', weight: 0.7, role: '정기' }, { stem: '갑', weight: 0.3, role: '중기' }]
        };
        return (data[branchName] || []).map((item: any) => ({
            ...item,
            stem: this.stemByName(item.stem)
        }));
    }

    public tenGod(dayStem: any, targetStem: any) {
        if (!dayStem || !targetStem) return '';
        let samePolarity = dayStem.yinYang === targetStem.yinYang;
        if (dayStem.element === targetStem.element) return samePolarity ? '비견' : '겁재';
        if (this.producesElement(dayStem.element) === targetStem.element) return samePolarity ? '식신' : '상관';
        if (this.controlsElement(dayStem.element) === targetStem.element) return samePolarity ? '편재' : '정재';
        if (this.controlledByElement(dayStem.element) === targetStem.element) return samePolarity ? '편관' : '정관';
        if (this.producerOf(dayStem.element) === targetStem.element) return samePolarity ? '편인' : '정인';
        return '';
    }

    public producesElement(element: string) {
        let flow: any = { 목: '화', 화: '토', 토: '금', 금: '수', 수: '목' };
        return flow[element] || '토';
    }

    public producerOf(element: string) {
        let flow: any = { 목: '수', 화: '목', 토: '화', 금: '토', 수: '금' };
        return flow[element] || '화';
    }

    public controlsElement(element: string) {
        let flow: any = { 목: '토', 토: '수', 수: '화', 화: '금', 금: '목' };
        return flow[element] || '수';
    }

    public controlledByElement(element: string) {
        let flow: any = { 목: '금', 화: '수', 토: '목', 금: '화', 수: '토' };
        return flow[element] || '목';
    }

    public twelveStage(dayStem: any, branchName: string) {
        let branchOrder: any = {
            갑: ['해', '자', '축', '인', '묘', '진', '사', '오', '미', '신', '유', '술'],
            을: ['오', '사', '진', '묘', '인', '축', '자', '해', '술', '유', '신', '미'],
            병: ['인', '묘', '진', '사', '오', '미', '신', '유', '술', '해', '자', '축'],
            무: ['인', '묘', '진', '사', '오', '미', '신', '유', '술', '해', '자', '축'],
            정: ['유', '신', '미', '오', '사', '진', '묘', '인', '축', '자', '해', '술'],
            기: ['유', '신', '미', '오', '사', '진', '묘', '인', '축', '자', '해', '술'],
            경: ['사', '오', '미', '신', '유', '술', '해', '자', '축', '인', '묘', '진'],
            신: ['자', '해', '술', '유', '신', '미', '오', '사', '진', '묘', '인', '축'],
            임: ['신', '유', '술', '해', '자', '축', '인', '묘', '진', '사', '오', '미'],
            계: ['묘', '인', '축', '자', '해', '술', '유', '신', '미', '오', '사', '진']
        };
        let stages = ['장생', '목욕', '관대', '건록', '제왕', '쇠', '병', '사', '묘', '절', '태', '양'];
        let index = (branchOrder[dayStem.name] || []).indexOf(branchName);
        return index >= 0 ? stages[index] : '';
    }

    public emptyBranches(dayCycleIndex: number) {
        let start = Math.floor(this.mod(dayCycleIndex, 60) / 10) * 10;
        return [
            this.branchData()[this.mod(start + 10, 12)].name,
            this.branchData()[this.mod(start + 11, 12)].name
        ];
    }

    public stemByName(name: string) {
        return this.stemData().find((stem: any) => stem.name === name) || this.stemData()[0];
    }

    public branchByName(name: string) {
        return this.branchData().find((branch: any) => branch.name === name) || this.branchData()[0];
    }

    public tenGodTone(label: string) {
        let tones: any = {
            비견: '자기 기준, 독립성, 동료 의식',
            겁재: '경쟁심, 추진력, 판을 흔드는 힘',
            식신: '표현력, 생산성, 꾸준한 결과물',
            상관: '개성, 말과 글, 틀을 깨는 감각',
            편재: '기회 포착, 활동성, 넓은 관계',
            정재: '관리력, 현실감, 안정적인 축적',
            편관: '압박 속 실행, 승부근성, 책임',
            정관: '규칙, 신뢰, 사회적 균형',
            편인: '직관, 연구, 낯선 지식 흡수',
            정인: '보호, 학습, 안정적 회복',
            일간: '나 자신과 기본 에너지'
        };
        return tones[label] || '명식의 주요 작용';
    }

    public stemData() {
        return [
            { index: 0, name: '갑', hanja: '甲', element: '목', yinYang: '양' },
            { index: 1, name: '을', hanja: '乙', element: '목', yinYang: '음' },
            { index: 2, name: '병', hanja: '丙', element: '화', yinYang: '양' },
            { index: 3, name: '정', hanja: '丁', element: '화', yinYang: '음' },
            { index: 4, name: '무', hanja: '戊', element: '토', yinYang: '양' },
            { index: 5, name: '기', hanja: '己', element: '토', yinYang: '음' },
            { index: 6, name: '경', hanja: '庚', element: '금', yinYang: '양' },
            { index: 7, name: '신', hanja: '辛', element: '금', yinYang: '음' },
            { index: 8, name: '임', hanja: '壬', element: '수', yinYang: '양' },
            { index: 9, name: '계', hanja: '癸', element: '수', yinYang: '음' }
        ];
    }

    public branchData() {
        return [
            { index: 0, name: '자', hanja: '子', animal: '쥐', element: '수' },
            { index: 1, name: '축', hanja: '丑', animal: '소', element: '토' },
            { index: 2, name: '인', hanja: '寅', animal: '호랑이', element: '목' },
            { index: 3, name: '묘', hanja: '卯', animal: '토끼', element: '목' },
            { index: 4, name: '진', hanja: '辰', animal: '용', element: '토' },
            { index: 5, name: '사', hanja: '巳', animal: '뱀', element: '화' },
            { index: 6, name: '오', hanja: '午', animal: '말', element: '화' },
            { index: 7, name: '미', hanja: '未', animal: '양', element: '토' },
            { index: 8, name: '신', hanja: '申', animal: '원숭이', element: '금' },
            { index: 9, name: '유', hanja: '酉', animal: '닭', element: '금' },
            { index: 10, name: '술', hanja: '戌', animal: '개', element: '토' },
            { index: 11, name: '해', hanja: '亥', animal: '돼지', element: '수' }
        ];
    }

    public elementCounts(pillars: any[]) {
        let counts: any = { 목: 0, 화: 0, 토: 0, 금: 0, 수: 0 };
        for (let pillar of pillars) {
            if (pillar?.unknown) continue;
            counts[pillar.stem.element] += 1;
            counts[pillar.branch.element] += 1;
        }
        return Object.keys(counts).map((label: string) => ({
            label: label,
            value: counts[label],
            tone: this.elementTone(label).core
        })).sort((a: any, b: any) => b.value - a.value);
    }

    public elementTone(element: string) {
        let tones: any = {
            목: { core: '성장과 시작을 밀어주는 기운', strength: '아이디어와 추진력', care: '새로운 자극보다 마무리 루틴', action: '작게 시작해서 계속 키우기' },
            화: { core: '표현과 확산이 빠른 기운', strength: '분위기를 밝히는 존재감', care: '충분한 휴식과 감정 온도 조절', action: '마음을 숨기지 않고 분명히 표현하기' },
            토: { core: '중심을 잡고 버티는 기운', strength: '안정감과 책임감', care: '몸을 움직여 흐름을 순환시키기', action: '기준을 세우고 차분히 쌓기' },
            금: { core: '정리와 판단이 선명한 기운', strength: '결정력과 완성도', care: '부드러운 말투와 여백', action: '복잡한 것을 덜어내고 핵심만 남기기' },
            수: { core: '생각과 감각이 깊은 기운', strength: '관찰력과 유연함', care: '햇빛, 대화, 가벼운 실행', action: '느낀 것을 기록하고 천천히 움직이기' }
        };
        return tones[element] || tones.토;
    }

    public parseBirthDate(value: string, calendarType: string = 'solar') {
        if (!value) return null;
        if (calendarType === 'lunar' || calendarType === 'lunarLeap') {
            let lunarDate = this.lunarToSolarDate(value, calendarType === 'lunarLeap');
            if (lunarDate) return lunarDate;
            return null;
        }
        let parts = value.split('-').map((part: string) => parseInt(part, 10));
        if (parts.length < 3 || parts.some((part: number) => Number.isNaN(part))) return null;
        let date = new Date(parts[0], parts[1] - 1, parts[2]);
        if (Number.isNaN(date.getTime())) return null;
        if (date.getFullYear() !== parts[0] || date.getMonth() !== parts[1] - 1 || date.getDate() !== parts[2]) return null;
        return date;
    }

    public lunarToSolarDate(value: string, leapMonth: boolean = false) {
        let parts = value.split('-').map((part: string) => parseInt(part, 10));
        if (parts.length < 3 || parts.some((part: number) => Number.isNaN(part))) return null;
        let year = parts[0];
        let month = parts[1];
        let day = parts[2];
        if (month < 1 || month > 12 || day < 1 || day > 30) return null;
        let start = new Date(year, 0, 1);
        let end = new Date(year + 1, 2, 15);

        for (let cursor = new Date(start.getTime()); cursor.getTime() <= end.getTime(); cursor.setDate(cursor.getDate() + 1)) {
            let info = this.lunarDateInfo(cursor);
            if (!info) return null;
            if (info.year === year && info.month === month && info.day === day && info.leapMonth === leapMonth) {
                return new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
            }
        }

        return null;
    }

    public lunarDateInfo(date: Date) {
        let locales = ['ko-u-ca-dangi', 'en-u-ca-chinese'];
        for (let locale of locales) {
            try {
                let formatter = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: 'Asia/Seoul' });
                let calendar = formatter.resolvedOptions().calendar;
                if (calendar !== 'dangi' && calendar !== 'chinese') continue;
                let parts = formatter.formatToParts(date);
                let monthText = parts.find((part: any) => part.type === 'month')?.value || '';
                let dayText = parts.find((part: any) => part.type === 'day')?.value || '';
                let yearText = parts.find((part: any) => part.type === 'relatedYear')?.value || parts.find((part: any) => part.type === 'year')?.value || '';
                let monthNumber = this.firstNumber(monthText);
                let dayNumber = this.firstNumber(dayText);
                let yearNumber = this.firstNumber(yearText);
                if (!monthNumber || !dayNumber || !yearNumber) continue;

                return {
                    year: yearNumber,
                    month: monthNumber,
                    day: dayNumber,
                    leapMonth: monthText.indexOf('윤') >= 0 || monthText.toLowerCase().indexOf('bis') >= 0 || monthText.toLowerCase().indexOf('leap') >= 0
                };
            } catch (error) {
                continue;
            }
        }
        return null;
    }

    public firstNumber(text: string) {
        let match = String(text || '').match(/\d+/);
        return match ? parseInt(match[0], 10) : 0;
    }

    public calendarTypeLabel(calendarType: string = 'solar') {
        if (calendarType === 'lunar') return '음력 평달 입력';
        if (calendarType === 'lunarLeap') return '음력 윤달 입력';
        return '양력 입력';
    }

    public daysSinceBase(date: Date) {
        let base = Date.UTC(1984, 1, 2);
        let current = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
        return Math.floor((current - base) / 86400000);
    }

    public formatInputDate(date: Date) {
        let year = date.getFullYear();
        let month = String(date.getMonth() + 1).padStart(2, '0');
        let day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    public formatKoreanDate(date: Date) {
        return `${date.getMonth() + 1}월 ${date.getDate()}일`;
    }

    public formatDateTime(date: Date) {
        let month = String(date.getMonth() + 1).padStart(2, '0');
        let day = String(date.getDate()).padStart(2, '0');
        let hour = String(date.getHours()).padStart(2, '0');
        let minute = String(date.getMinutes()).padStart(2, '0');
        return `${date.getFullYear()}-${month}-${day} ${hour}:${minute}`;
    }

    public clamp(value: number, min: number, max: number) {
        return Math.max(min, Math.min(max, value));
    }

    public mod(value: number, size: number) {
        return ((value % size) + size) % size;
    }
}
